<template>
  <canvas ref="canvas" class="water-ripple-bg"></canvas>
</template>

<script>
/**
 * WaterRippleBackground — 全屏底层水纹流动背景（重构版）
 *
 * 多层正弦波纹叠加：横向 + 纵向 + 斜向，各层独立振幅/频率/相位/速度
 * 振幅 28~40，增加水面折射扭曲感
 * 低透明度青蓝色辉光线条（0.10~0.14），不遮挡页面内容
 * Canvas 只绘制水纹线条，不填充底色，z-index:-1 固定底层
 * requestAnimationFrame 驱动，resize 自适应重绘
 */
export default {
  name: 'WaterRippleBackground',
  data () {
    return {
      rafId: null,
      w: 0,
      h: 0,
      dpr: 1,
      t: 0,
      // 水纹层配置：type=h 横向 / v 纵向 / d 斜向
      // 每层独立振幅(28~40)、频率、速度、相位、透明度(0.10~0.14)
      layers: [
        // ── 横向波纹（沿 x 轴展开，y 值起伏） ──
        { type: 'h', amp: 36, freq: 0.0032, speed: 0.0006, phase: 0.0, alpha: 0.14, color: '64, 196, 224', yRatio: 0.15 },
        { type: 'h', amp: 30, freq: 0.0050, speed: -0.0009, phase: 1.8, alpha: 0.12, color: '90, 210, 240', yRatio: 0.32 },
        { type: 'h', amp: 40, freq: 0.0024, speed: 0.0005, phase: 3.2, alpha: 0.13, color: '56, 178, 220', yRatio: 0.50 },
        { type: 'h', amp: 28, freq: 0.0060, speed: -0.0011, phase: 0.5, alpha: 0.10, color: '100, 220, 255', yRatio: 0.68 },
        { type: 'h', amp: 34, freq: 0.0038, speed: 0.0008, phase: 2.5, alpha: 0.11, color: '70, 200, 230', yRatio: 0.85 },
        // ── 纵向波纹（沿 y 轴展开，x 值起伏） ──
        { type: 'v', amp: 32, freq: 0.0036, speed: 0.0007, phase: 1.2, alpha: 0.12, color: '80, 200, 235', xRatio: 0.12 },
        { type: 'v', amp: 28, freq: 0.0055, speed: -0.0010, phase: 3.5, alpha: 0.10, color: '100, 215, 245', xRatio: 0.38 },
        { type: 'v', amp: 36, freq: 0.0028, speed: 0.0006, phase: 0.8, alpha: 0.11, color: '60, 190, 225', xRatio: 0.62 },
        { type: 'v', amp: 30, freq: 0.0048, speed: -0.0008, phase: 2.0, alpha: 0.10, color: '90, 210, 240', xRatio: 0.88 },
        // ── 斜向波纹（增强水面流动起伏感，沿对角线方向展开） ──
        { type: 'd', amp: 30, freq: 0.0034, speed: 0.0007, phase: 1.0, alpha: 0.12, color: '64, 200, 230', angle: 30 },
        { type: 'd', amp: 34, freq: 0.0042, speed: -0.0009, phase: 2.8, alpha: 0.11, color: '80, 210, 240', angle: 150 },
        { type: 'd', amp: 28, freq: 0.0028, speed: 0.0005, phase: 4.2, alpha: 0.10, color: '100, 220, 255', angle: 60 }
      ]
    }
  },
  mounted () {
    this.resize()
    window.addEventListener('resize', this.resize)
    this.animate()
  },
  beforeUnmount () {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    window.removeEventListener('resize', this.resize)
  },
  methods: {
    resize () {
      this.dpr = Math.min(window.devicePixelRatio || 1, 2)
      this.w = window.innerWidth
      this.h = window.innerHeight
      const c = this.$refs.canvas
      c.width = this.w * this.dpr
      c.height = this.h * this.dpr
      c.style.width = this.w + 'px'
      c.style.height = this.h + 'px'
      const ctx = c.getContext('2d')
      ctx.scale(this.dpr, this.dpr)
    },
    animate () {
      this.t += 16
      this.draw()
      this.rafId = requestAnimationFrame(this.animate)
    },
    draw () {
      const c = this.$refs.canvas
      if (!c) return
      const ctx = c.getContext('2d')
      // 清空画布：只清像素，不填充底色
      ctx.clearRect(0, 0, this.w, this.h)

      const T = this.t
      for (const L of this.layers) {
        ctx.beginPath()
        ctx.strokeStyle = `rgba(${L.color}, ${L.alpha})`
        ctx.lineWidth = 1.4
        ctx.lineCap = 'round'
        // 微弱辉光：shadowBlur 让线条带轻微发散
        ctx.shadowColor = `rgba(${L.color}, ${L.alpha * 0.5})`
        ctx.shadowBlur = 4

        if (L.type === 'h') {
          // 横向波纹：沿 x 轴展开，y 值随多重正弦波动
          const baseY = this.h * L.yRatio
          const step = 3
          for (let x = 0; x <= this.w; x += step) {
            const y = baseY +
              Math.sin(x * L.freq + T * L.speed + L.phase) * L.amp +
              Math.sin(x * L.freq * 2.3 + T * L.speed * 1.6 + L.phase * 0.5) * L.amp * 0.35 +
              Math.sin(x * L.freq * 0.5 + T * L.speed * 0.8) * L.amp * 0.2
            if (x === 0) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
        } else if (L.type === 'v') {
          // 纵向波纹：沿 y 轴展开，x 值随多重正弦波动
          const baseX = this.w * L.xRatio
          const step = 3
          for (let y = 0; y <= this.h; y += step) {
            const x = baseX +
              Math.sin(y * L.freq + T * L.speed + L.phase) * L.amp +
              Math.sin(y * L.freq * 2.1 + T * L.speed * 1.7 + L.phase * 0.5) * L.amp * 0.35 +
              Math.sin(y * L.freq * 0.5 + T * L.speed * 0.8) * L.amp * 0.2
            if (y === 0) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
        } else if (L.type === 'd') {
          // 斜向波纹：沿对角线方向展开，增加水面流动起伏感
          const rad = L.angle * Math.PI / 180
          const cos = Math.cos(rad)
          const sin = Math.sin(rad)
          // 投影长度范围
          const maxProj = Math.max(this.w, this.h) * 1.5
          const step = 3
          for (let p = -maxProj; p <= maxProj; p += step) {
            // 中心偏移：让斜线大致居中
            const cx = this.w * 0.5
            const cy = this.h * 0.5
            // 沿对角线方向坐标
            let x = cx + p * cos
            let y = cy + p * sin
            // 垂直于对角线方向叠加正弦波（折射扭曲感）
            const wave = Math.sin(p * L.freq + T * L.speed + L.phase) * L.amp +
              Math.sin(p * L.freq * 2.2 + T * L.speed * 1.5 + L.phase * 0.5) * L.amp * 0.35 +
              Math.sin(p * L.freq * 0.5 + T * L.speed * 0.7) * L.amp * 0.2
            x += -sin * wave
            y += cos * wave
            if (p === -maxProj) ctx.moveTo(x, y)
            else ctx.lineTo(x, y)
          }
        }
        ctx.stroke()
      }
      // 重置 shadow 避免影响后续
      ctx.shadowBlur = 0
    }
  }
}
</script>

<style scoped>
.water-ripple-bg {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
}
</style>
